import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Trash2, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { EmptyState } from '../components/common/EmptyState';
import { Skeleton } from '../components/ui/skeleton';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import UpgradePrompt from '../components/common/UpgradePrompt';
import { getTier1Languages } from '../lib/languages';

/**
 * VirtualOfficesPage Component
 * Per PocketFiler UI Style Guide - uses SVG icons from asset pack, no emojis
 */

// SVG Icon component
const SvgIcon = ({ src, className = 'h-5 w-5' }) => (
  <img src={`/assets/${src}`} alt="" className={className} aria-hidden="true" />
);

const industries = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'import_export', label: 'Import/Export' },
  { value: 'other', label: 'Other' }
];

export default function VirtualOfficesPage() {
  const { t, language } = useTranslation();
  const tier1Languages = getTier1Languages();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Basic details, 2: Review checklist, 3: Save
  const [generatingChecklist, setGeneratingChecklist] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    industry: 'manufacturing',
    languages: [language],
    description: '',
    document_checklist: []
  });
  const [newChecklistItem, setNewChecklistItem] = useState('');

  const { data: offices, isLoading } = useQuery({
    queryKey: ['virtual-offices'],
    queryFn: () => api.get('/virtual-offices')
  });

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get('/clients')
  });

  const { data: orgPlanInfo } = useQuery({
    queryKey: ['org-plan', user?.organization_id],
    queryFn: () => api.get(`/organizations/${user.organization_id}/plan`),
    enabled: !!user?.organization_id
  });

  const hasLimit = true; // All plans check limits (backend enforces)
  const officeLimit = orgPlanInfo?.limits?.max_offices ?? -1;
  const officeCount = offices?.length ?? 0;
  const atOfficeLimit = hasLimit && officeLimit !== -1 && officeCount >= officeLimit;

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/virtual-offices', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['virtual-offices']);
      toast.success(t('success.created'));
      setDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message || t('error.generic'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/virtual-offices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['virtual-offices']);
      toast.success(t('success.deleted'));
    }
  });

  const resetForm = () => {
    setFormData({ name: '', industry: 'manufacturing', languages: ['en'], description: '', document_checklist: [] });
    setStep(1);
  };

  const toggleLanguage = (code) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(code)
        ? prev.languages.filter(l => l !== code)
        : [...prev.languages, code]
    }));
  };

  const generateAIChecklist = async () => {
    if (!formData.industry) {
      toast.error('Please select an industry first');
      return;
    }
    
    setGeneratingChecklist(true);
    try {
      const response = await api.post('/ai/office-template', {
        industry: formData.industry,
        primary_language: formData.languages[0] || 'en',
        description: formData.description
      });
      
      if (response.document_checklist) {
        setFormData(prev => ({
          ...prev,
          document_checklist: response.document_checklist.map(item => 
            typeof item === 'string' ? item : item.name
          ),
          name: prev.name || response.suggested_name || ''
        }));
        toast.success('AI checklist generated!');
        setStep(2);
      }
    } catch (error) {
      toast.error('Failed to generate checklist');
    } finally {
      setGeneratingChecklist(false);
    }
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData(prev => ({
        ...prev,
        document_checklist: [...prev.document_checklist, newChecklistItem.trim()]
      }));
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index) => {
    setFormData(prev => ({
      ...prev,
      document_checklist: prev.document_checklist.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter an office name');
      return;
    }
    createMutation.mutate(formData);
  };

  const getOfficeStats = (officeId) => {
    const officeClients = clients?.filter(c => c.virtual_office_id === officeId) || [];
    return {
      totalClients: officeClients.length,
      pendingClients: officeClients.filter(c => ['sent', 'opened', 'in_progress'].includes(c.invite_status)).length
    };
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">{t("virtualoffices.virtualOffices")}</h1>
          <p className="text-muted-foreground">{t("virtualoffices.manageYourBrandedWorkspaces")}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasLimit && officeLimit !== -1 && (
            <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
              {officeCount} / {officeLimit} office
            </span>
          )}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-office-button"
                disabled={atOfficeLimit}
                title={atOfficeLimit ? `Office limit reached (${officeLimit} on current plan)` : undefined}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("virtualoffices.newOffice") || "New Office"}
              </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {step === 1 && t("virtualoffices.createOffice") || "Create Virtual Office"}
                {step === 2 && t("virtualoffices.reviewChecklist") || "Review Document Checklist"}
              </DialogTitle>
              <DialogDescription>
                {step === 1 && t("virtualoffices.setupDesc") || "Set up a new branded workspace for your business"}
                {step === 2 && t("virtualoffices.customizeDesc") || "Customize the documents you need from clients"}
              </DialogDescription>
            </DialogHeader>

            {step === 1 && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("virtualoffices.officeName")}</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Garcia Insurance – Auto Policies"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="office-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("virtualoffices.industry")}</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
                  >
                    <SelectTrigger data-testid="office-industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("virtualoffices.languagesServed")}</Label>
                  <div className="flex flex-wrap gap-2">
                    {tier1Languages.map(lang => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => toggleLanguage(lang.code)}
                        data-testid="office-language-chip"
                        className={`language-chip cursor-pointer transition-colors ${
                          formData.languages.includes(lang.code)
                            ? 'bg-accent text-accent-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t("virtualoffices.descriptionOptional")}</Label>
                  <Textarea
                    id="description"
                    placeholder={t("virtualoffices.ph.description") || "Describe your office focus..."}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>
                <DialogFooter className="pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {t("common.cancel") || "Cancel"}
                  </Button>
                  <Button 
                    type="button" 
                    onClick={generateAIChecklist}
                    disabled={generatingChecklist}
                  >
                    {generatingChecklist ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t("virtualoffices.generating") || "Generating..."}
                      </>
                    ) : (
                      <>
                        <SvgIcon src="bulb.svg" className="h-4 w-4 mr-2" />
                        Generate Checklist
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="flex items-center gap-2 text-sm text-accent">
                      <SvgIcon src="bulb.svg" className="h-4 w-4" />
                      {t("virtualoffices.aiChecklist") || "AI-generated checklist for"} {formData.industry.replace('_', ' ')}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("virtualoffices.documentChecklist")}</Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {formData.document_checklist.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 rounded border bg-card" data-testid="office-checklist-item">
                          <Checkbox checked disabled />
                          <span className="flex-1 text-sm">{item}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeChecklistItem(index)}
                            aria-label="Remove checklist item"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder={t("virtualoffices.ph.addDocument") || "Add custom document..."}
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                      />
                      <Button type="button" variant="outline" onClick={addChecklistItem}>
                        Add
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateAIChecklist}
                    disabled={generatingChecklist}
                  >
                    <SvgIcon src="bulb.svg" className="h-4 w-4 mr-1" />
                    Re-generate checklist
                  </Button>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Office'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Plan upgrade prompt when at limit */}
      {atOfficeLimit && (
        <UpgradePrompt
          feature="Creating more virtual offices"
          message={`You've reached the ${officeLimit}-office limit on your current plan. Upgrade for more.`}
        />
      )}

      {/* Offices Grid */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : offices && offices.length > 0 ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {offices.map(office => {
            const stats = getOfficeStats(office._id);
            return (
              <Card key={office._id} className="hover:shadow-md transition-shadow group" data-testid="office-card">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <SvgIcon src="organization.svg" className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{office.name}</CardTitle>
                        <CardDescription className="capitalize">{office.industry?.replace('_', ' ')}</CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:bg-destructive/10"
                      onClick={() => deleteMutation.mutate(office._id)}
                      aria-label="Delete virtual office"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <SvgIcon src="user.svg" className="h-4 w-4" />
                          {t("virtualoffices.clients") || "Clients"}
                        </div>
                        <p className="text-xl font-semibold">{stats.totalClients}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <SvgIcon src="hourglass.svg" className="h-4 w-4" />
                          {t("virtualoffices.pending") || "Pending"}
                        </div>
                        <p className="text-xl font-semibold">{stats.pendingClients}</p>
                      </div>
                    </div>

                    {/* Languages */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">{t("virtualoffices.languages")}</p>
                      <div className="flex flex-wrap gap-1">
                        {office.languages?.map(lang => (
                          <span key={lang} className="language-chip" data-testid="office-language-chip">
                            {lang.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Checklist preview */}
                    {office.document_checklist?.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">{t("virtualoffices.documentChecklist") || "Document Checklist"} ({office.document_checklist.length})</p>
                        <div className="space-y-1">
                          {office.document_checklist.slice(0, 2).map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Checkbox checked={false} disabled className="h-3 w-3" />
                              <span className="truncate">{item}</span>
                            </div>
                          ))}
                          {office.document_checklist.length > 2 && (
                            <p className="text-xs text-muted-foreground pl-5">+{office.document_checklist.length - 2} more</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/clients?office=${office._id}`)}
                      >
                        <SvgIcon src="user.svg" className="h-4 w-4 mr-1" />
                        {t("virtualoffices.clients") || "Clients"}
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/clients?office=${office._id}&action=invite`)}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {t("virtualoffices.invite") || "Invite"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          svgIcon="organization.svg"
          title="No virtual offices yet"
          description="Create your first virtual office to start organizing your business"
          action={
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Office
            </Button>
          }
        />
      )}
    </div>
  );
}
